import { useEffect, useState } from "react";
import { LogOut, Mail, GraduationCap, Users, ShoppingBag, Inbox, CheckCircle2, Clock, CalendarDays, Trash2, Plus, ShieldCheck, KeyRound, HandHeart, EyeOff, Send, Award, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import HBTVAdmin from "@/components/admin/HBTVAdmin";
import AdminHealth from "@/components/admin/AdminHealth";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [interests, setInterests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [devotionalSchedule, setDevotionalSchedule] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const emptyEvent = { title: "", type: "Conference", date: "", time: "", location: "", description: "", image: "", featured: false };
  const [newEvent, setNewEvent] = useState(emptyEvent);
  const emptyPwd = { current_password: "", new_password: "", confirm_password: "" };
  const [pwd, setPwd] = useState(emptyPwd);
  const [pwdSaving, setPwdSaving] = useState(false);

  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  const loadEvents = () => api.get("/admin/events").then(({ data }) => setEvents(data)).catch(() => {});
  const loadSubscribers = () => api.get("/admin/newsletter").then(({ data }) => setSubscribers(data)).catch(() => {});
  const loadSchedule = () => api.get("/admin/devotional/schedule").then(({ data }) => setDevotionalSchedule(data)).catch(() => {});
  const loadPrayers = () => api.get("/admin/prayer").then(({ data }) => setPrayers(data)).catch(() => {});
  const loadTestimonies = () => api.get("/admin/testimonies").then(({ data }) => setTestimonies(data)).catch(() => {});

  useEffect(() => {
    loadStats();
    api.get("/admin/contacts").then(({ data }) => setContacts(data)).catch(() => {});
    api.get("/admin/interests").then(({ data }) => setInterests(data)).catch(() => {});
    loadOrders();
    loadEvents();
    loadSubscribers();
    loadSchedule();
    loadPrayers();
    loadTestimonies();
  }, []);

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/events", newEvent);
      toast.success("Event added.");
      setNewEvent(emptyEvent);
      loadEvents();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteEvent = async (id) => {
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event removed.");
      loadEvents();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const confirmOrder = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/confirm`);
      toast.success("Order confirmed. Access granted.");
      loadOrders();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm_password) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setPwdSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      if (data.token) localStorage.setItem("abj_token", data.token);
      toast.success("Password updated. Keep it safe.");
      setPwd(emptyPwd);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwdSaving(false);
    }
  };

  const moderatePrayer = async (id, action) => {
    try {
      if (action === "delete") {
        await api.delete(`/admin/prayer/${id}`);
        toast.success("Prayer request deleted.");
      } else {
        await api.post(`/admin/prayer/${id}/${action}`);
        toast.success(`Request ${action === "approve" ? "approved" : "hidden"}.`);
      }
      loadPrayers();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const moderateTestimony = async (id, action) => {
    try {
      if (action === "delete") {
        await api.delete(`/admin/testimonies/${id}`);
        toast.success("Testimony deleted.");
      } else if (action === "feature") {
        const { data } = await api.post(`/admin/testimonies/${id}/feature`);
        toast.success(data.is_featured ? "Pinned as featured." : "Unpinned.");
      } else {
        await api.post(`/admin/testimonies/${id}/${action}`);
        toast.success(`Testimony ${action === "approve" ? "approved" : "hidden"}.`);
      }
      loadTestimonies();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendDevotional = async () => {
    if (!window.confirm("Send today's devotional to all newsletter subscribers now?")) return;
    try {
      const { data } = await api.post("/admin/devotional/send");
      toast.success(data.message || `Sent to ${data.sent} of ${data.total}.`);
      loadSchedule();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const pendingOrders = orders.filter((o) => o.status === "pending_review").length;

  const cards = stats ? [
    { label: "Messages", value: stats.contacts, icon: Mail },
    { label: "Course Interest", value: stats.interests, icon: GraduationCap },
    { label: "Members", value: stats.members, icon: Users },
    { label: "Enrollments", value: stats.enrollments, icon: Inbox },
    { label: "Card Orders", value: stats.orders, icon: ShoppingBag },
    { label: "Pending Orders", value: stats.manual_orders, icon: Clock },
    { label: "Events", value: stats.events, icon: CalendarDays },
    { label: "RSVPs", value: stats.rsvps, icon: Inbox },
    { label: "Subscribers", value: stats.subscribers || 0, icon: Send },
    { label: "Prayers", value: stats.prayers || 0, icon: HandHeart },
    { label: "Testimonies", value: stats.testimonies || 0, icon: Award },
  ] : [];

  return (
    <div className="pt-32 md:pt-40 pb-28 min-h-screen" data-testid="admin-page">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 border-b border-gold-500/20 pb-10">
          <div>
            <Overline>Ministry Admin</Overline>
            <h1 className="mt-4 font-serif text-5xl md:text-6xl text-white">Dashboard</h1>
            <p className="mt-3 text-white/60">Signed in as {user?.email}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-gold-500/30 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-white/70 hover:border-gold-400 hover:text-gold-300 transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </Reveal>

        {/* Stats */}
        <div className="mt-12 grid gap-px bg-gold-500/15 border border-gold-500/15 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
          {cards.map((c) => (
            <div key={c.label} className="bg-ink p-7">
              <c.icon size={20} className="text-gold-500" strokeWidth={1.5} />
              <p className="mt-6 font-serif text-4xl text-white">{c.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-white/50">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Tables */}
        <div className="mt-14">
          <AdminHealth />
          <Tabs defaultValue="contacts">
            <TabsList className="bg-ink-paper border border-gold-500/20">
              <TabsTrigger value="contacts" data-testid="tab-contacts" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                Contact Messages ({contacts.length})
              </TabsTrigger>
              <TabsTrigger value="interests" data-testid="tab-interests" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                Course Interest ({interests.length})
              </TabsTrigger>
              <TabsTrigger value="orders" data-testid="tab-orders" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                Mobile Money Orders ({pendingOrders})
              </TabsTrigger>
              <TabsTrigger value="events" data-testid="tab-events" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                Events ({events.length})
              </TabsTrigger>
              <TabsTrigger value="prayers" data-testid="tab-prayers" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                Prayer Wall ({prayers.length})
              </TabsTrigger>
              <TabsTrigger value="testimonies" data-testid="tab-testimonies" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                Testimonies ({testimonies.length})
              </TabsTrigger>
              <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                Newsletter ({subscribers.length})
              </TabsTrigger>
              <TabsTrigger value="hbtv" data-testid="tab-hbtv" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                HBTV
              </TabsTrigger>
              <TabsTrigger value="security" data-testid="tab-security" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70">
                Security
              </TabsTrigger>
            </TabsList>

            <TabsContent value="contacts" className="mt-8">
              {contacts.length === 0 ? (
                <p className="text-white/50 py-10 text-center border border-gold-500/15">No messages yet.</p>
              ) : (
                <div className="space-y-4">
                  {contacts.map((c) => (
                    <div key={c.id} className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid={`contact-row-${c.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-serif text-xl text-white">{c.subject}</p>
                          <p className="text-sm text-gold-400 mt-1">{c.name} · {c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                        </div>
                        <span className="text-xs text-white/40">{fmtDate(c.created_at)}</span>
                      </div>
                      {c.interest && <span className="inline-block mt-3 border border-gold-500/30 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-white/70">{c.interest}</span>}
                      <p className="mt-4 text-sm text-white/70 leading-relaxed">{c.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="interests" className="mt-8">
              {interests.length === 0 ? (
                <p className="text-white/50 py-10 text-center border border-gold-500/15">No course interest yet.</p>
              ) : (
                <div className="space-y-3">
                  {interests.map((it) => (
                    <div key={it.id} className="flex flex-wrap items-center justify-between gap-3 border border-gold-500/20 bg-ink-paper/40 p-5" data-testid={`interest-row-${it.id}`}>
                      <div>
                        <p className="text-white font-medium">{it.name} <span className="text-white/50 font-normal">· {it.email}</span></p>
                        <p className="text-sm text-gold-400 mt-1">Interested in: {it.course_title}</p>
                      </div>
                      <span className="text-xs text-white/40">{fmtDate(it.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="orders" className="mt-8">
              {orders.length === 0 ? (
                <p className="text-white/50 py-10 text-center border border-gold-500/15">No Mobile Money / bank orders yet.</p>
              ) : (
                <div className="space-y-3">
                  {orders.map((o) => (
                    <div key={o.id} className="flex flex-wrap items-center justify-between gap-4 border border-gold-500/20 bg-ink-paper/40 p-5" data-testid={`order-row-${o.id}`}>
                      <div className="min-w-0">
                        <p className="text-white font-medium">{o.title} <span className="text-gold-400">· ${o.amount}</span></p>
                        <p className="text-sm text-white/60 mt-1">{o.name} · {o.email} · {o.phone}</p>
                        <p className="text-xs text-white/45 mt-1">
                          {String(o.method).toUpperCase()} · Ref <span className="text-gold-300">{o.reference}</span> · {fmtDate(o.created_at)}
                        </p>
                      </div>
                      {o.status === "confirmed" ? (
                        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-gold-400">
                          <CheckCircle2 size={15} /> Confirmed
                        </span>
                      ) : (
                        <button
                          onClick={() => confirmOrder(o.id)}
                          data-testid={`confirm-order-${o.id}`}
                          className="inline-flex items-center gap-2 bg-gold-500 px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold text-ink hover:bg-gold-400 transition-colors"
                        >
                          <CheckCircle2 size={14} /> Confirm
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="events" className="mt-8">
              <form onSubmit={createEvent} className="border border-gold-500/20 bg-ink-paper/40 p-6 mb-8 grid gap-3 sm:grid-cols-2" data-testid="event-form">
                <Input required placeholder="Event title" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} data-testid="event-title" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
                <select value={newEvent.type} onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })} data-testid="event-type" className="bg-ink border border-gold-500/25 text-white/90 px-3 py-2 text-sm outline-none focus:border-gold-400">
                  {["Conference", "Service", "Training", "Online"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <Input required type="date" value={newEvent.date} onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })} data-testid="event-date" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
                <Input required placeholder="Time (e.g. 10:00 AM)" value={newEvent.time} onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })} data-testid="event-time" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
                <Input required placeholder="Location" value={newEvent.location} onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })} data-testid="event-location" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 sm:col-span-2" />
                <Input placeholder="Image URL (optional)" value={newEvent.image} onChange={(e) => setNewEvent({ ...newEvent, image: e.target.value })} data-testid="event-image" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 sm:col-span-2" />
                <Input required placeholder="Short description" value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} data-testid="event-description" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 sm:col-span-2" />
                <Button type="submit" data-testid="event-submit" className="bg-gold-500 text-ink hover:bg-gold-400 font-semibold sm:col-span-2">
                  <Plus size={15} className="mr-1" /> Add Event
                </Button>
              </form>

              {events.length === 0 ? (
                <p className="text-white/50 py-10 text-center border border-gold-500/15">No events yet.</p>
              ) : (
                <div className="space-y-3">
                  {events.map((ev) => (
                    <div key={ev.id} className="flex flex-wrap items-center justify-between gap-4 border border-gold-500/20 bg-ink-paper/40 p-5" data-testid={`admin-event-${ev.id}`}>
                      <div className="min-w-0">
                        <p className="text-white font-medium flex items-center gap-2">
                          <CalendarDays size={15} className="text-gold-500" /> {ev.title}
                          <span className="text-[10px] uppercase tracking-[0.15em] text-gold-400 border border-gold-500/30 px-2 py-0.5">{ev.type}</span>
                        </p>
                        <p className="text-sm text-white/60 mt-1">{fmtDate(ev.date)} · {ev.time} · {ev.location}</p>
                        <p className="text-xs text-gold-300 mt-1">{ev.rsvp_count || 0} registered</p>
                      </div>
                      <button onClick={() => deleteEvent(ev.id)} data-testid={`delete-event-${ev.id}`} className="inline-flex items-center gap-2 border border-red-500/30 text-red-300 px-4 py-2 text-xs hover:bg-red-500/10 transition-colors">
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="prayers" className="mt-8">
              {prayers.length === 0 ? (
                <p className="text-white/50 py-10 text-center border border-gold-500/15">No prayer requests yet.</p>
              ) : (
                <div className="space-y-4" data-testid="admin-prayers-list">
                  {prayers.map((p) => (
                    <div key={p.id} className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid={`admin-prayer-${p.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30 px-2 py-0.5">{p.category}</span>
                            {p.is_private && (
                              <span className="text-[10px] uppercase tracking-[0.2em] text-red-300 border border-red-500/30 px-2 py-0.5 inline-flex items-center gap-1">
                                <EyeOff size={10} /> Private
                              </span>
                            )}
                            {p.is_anonymous && (
                              <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 border border-white/20 px-2 py-0.5">Anonymous</span>
                            )}
                            {p.status !== "approved" && (
                              <span className="text-[10px] uppercase tracking-[0.2em] text-yellow-300 border border-yellow-500/30 px-2 py-0.5">{p.status}</span>
                            )}
                          </div>
                          <p className="mt-2 text-sm text-gold-400">
                            {p.real_name || p.name}
                            {p.email ? <span className="text-white/50"> · {p.email}</span> : null}
                          </p>
                        </div>
                        <span className="text-xs text-white/40">{fmtDate(p.created_at)} · {p.pray_count} prayers</span>
                      </div>
                      <p className="mt-4 text-sm text-white/75 leading-relaxed whitespace-pre-wrap">{p.message}</p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {p.status !== "approved" && (
                          <button
                            onClick={() => moderatePrayer(p.id, "approve")}
                            data-testid={`approve-prayer-${p.id}`}
                            className="inline-flex items-center gap-1.5 border border-gold-500/40 text-gold-300 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-gold-500/10"
                          >
                            <CheckCircle2 size={13} /> Approve
                          </button>
                        )}
                        {p.status === "approved" && (
                          <button
                            onClick={() => moderatePrayer(p.id, "hide")}
                            data-testid={`hide-prayer-${p.id}`}
                            className="inline-flex items-center gap-1.5 border border-yellow-500/40 text-yellow-300 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-yellow-500/10"
                          >
                            <EyeOff size={13} /> Hide
                          </button>
                        )}
                        <button
                          onClick={() => moderatePrayer(p.id, "delete")}
                          data-testid={`delete-prayer-${p.id}`}
                          className="inline-flex items-center gap-1.5 border border-red-500/30 text-red-300 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-red-500/10"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="testimonies" className="mt-8">
              {testimonies.length === 0 ? (
                <p className="text-white/50 py-10 text-center border border-gold-500/15">No testimonies yet.</p>
              ) : (
                <div className="space-y-4" data-testid="admin-testimonies-list">
                  {testimonies.map((t) => (
                    <div key={t.id} className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid={`admin-testimony-${t.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30 px-2 py-0.5">{t.category}</span>
                            {t.is_featured && (
                              <span className="text-[10px] uppercase tracking-[0.2em] text-ink bg-gold-500 px-2 py-0.5 inline-flex items-center gap-1">
                                <Star size={10} fill="currentColor" /> Featured{t.role ? ` · ${t.role}` : ""}
                              </span>
                            )}
                            {t.is_anonymous && <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 border border-white/20 px-2 py-0.5">Anonymous</span>}
                            {t.status !== "approved" && <span className="text-[10px] uppercase tracking-[0.2em] text-yellow-300 border border-yellow-500/30 px-2 py-0.5">{t.status}</span>}
                          </div>
                          <p className="mt-2 font-serif text-lg text-white">{t.title}</p>
                          <p className="mt-1 text-sm text-gold-400">
                            {t.real_name || t.name}
                            {t.location ? <span className="text-white/50"> · {t.location}</span> : null}
                            {t.email ? <span className="text-white/50"> · {t.email}</span> : null}
                          </p>
                        </div>
                        <span className="text-xs text-white/40">{fmtDate(t.created_at)} · {t.amen_count} amens</span>
                      </div>
                      <p className="mt-4 text-sm text-white/75 leading-relaxed whitespace-pre-wrap">{t.message}</p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        <button
                          onClick={() => moderateTestimony(t.id, "feature")}
                          data-testid={`feature-testimony-${t.id}`}
                          className={`inline-flex items-center gap-1.5 border px-4 py-2 text-xs uppercase tracking-[0.15em] ${
                            t.is_featured
                              ? "border-gold-500 bg-gold-500/20 text-gold-200"
                              : "border-white/20 text-white/70 hover:border-gold-500/50 hover:text-gold-300"
                          }`}
                        >
                          <Star size={13} fill={t.is_featured ? "currentColor" : "none"} />
                          {t.is_featured ? "Unpin" : "Feature"}
                        </button>
                        {t.status !== "approved" && (
                          <button
                            onClick={() => moderateTestimony(t.id, "approve")}
                            data-testid={`approve-testimony-${t.id}`}
                            className="inline-flex items-center gap-1.5 border border-gold-500/40 text-gold-300 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-gold-500/10"
                          >
                            <CheckCircle2 size={13} /> Approve
                          </button>
                        )}
                        {t.status === "approved" && (
                          <button
                            onClick={() => moderateTestimony(t.id, "hide")}
                            data-testid={`hide-testimony-${t.id}`}
                            className="inline-flex items-center gap-1.5 border border-yellow-500/40 text-yellow-300 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-yellow-500/10"
                          >
                            <EyeOff size={13} /> Hide
                          </button>
                        )}
                        <button
                          onClick={() => moderateTestimony(t.id, "delete")}
                          data-testid={`delete-testimony-${t.id}`}
                          className="inline-flex items-center gap-1.5 border border-red-500/30 text-red-300 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-red-500/10"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="newsletter" className="mt-8">
              <div className="mb-6 border border-gold-500/25 bg-ink-paper/40 p-6" data-testid="devotional-blast-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-serif text-xl text-white flex items-center gap-2">
                      <Sparkles size={18} className="text-gold-500" /> Daily Devotional Blast
                    </p>
                    <p className="mt-1 text-sm text-white/60">
                      Sends today's AI devotional to all {subscribers.length} active subscriber{subscribers.length === 1 ? "" : "s"}.
                    </p>
                  </div>
                  <button
                    onClick={sendDevotional}
                    disabled={subscribers.length === 0}
                    data-testid="send-devotional-btn"
                    className="inline-flex items-center gap-2 bg-gold-500 px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-ink hover:bg-gold-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send size={14} /> Send Now
                  </button>
                </div>
                {devotionalSchedule && (
                  <div className="mt-5 pt-5 border-t border-gold-500/15 grid gap-4 sm:grid-cols-3 text-sm" data-testid="devotional-schedule-info">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gold-400">Auto-send</p>
                      <p className="mt-1 text-white font-medium">
                        {devotionalSchedule.auto_send ? (
                          <span className="inline-flex items-center gap-1.5 text-gold-300">
                            <CheckCircle2 size={14} /> ON · Daily at {String(devotionalSchedule.hour_utc).padStart(2,"0")}:00 UTC
                          </span>
                        ) : (
                          <span className="text-white/50">OFF</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gold-400">Next Run</p>
                      <p className="mt-1 text-white/80">
                        {devotionalSchedule.next_run_utc ? fmtDate(devotionalSchedule.next_run_utc) : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gold-400">Last Blast</p>
                      <p className="mt-1 text-white/80">
                        {devotionalSchedule.history?.[0]
                          ? `${devotionalSchedule.history[0].sent}/${devotionalSchedule.history[0].total} · ${fmtDate(devotionalSchedule.history[0].created_at)}`
                          : "No blasts yet"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {subscribers.length === 0 ? (
                <p className="text-white/50 py-10 text-center border border-gold-500/15">No subscribers yet.</p>
              ) : (
                <div className="border border-gold-500/20 bg-ink-paper/40" data-testid="newsletter-list">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-white/50 border-b border-gold-500/15">
                        <th className="px-6 py-4">Email</th>
                        <th className="px-6 py-4">Name</th>
                        <th className="px-6 py-4">Subscribed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((s) => (
                        <tr key={s.id} className="border-b border-gold-500/10 last:border-0" data-testid={`sub-row-${s.id}`}>
                          <td className="px-6 py-4 text-white">{s.email}</td>
                          <td className="px-6 py-4 text-white/70">{s.name || "—"}</td>
                          <td className="px-6 py-4 text-white/50">{fmtDate(s.subscribed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="hbtv" className="mt-8">
              <HBTVAdmin />
            </TabsContent>

            <TabsContent value="security" className="mt-8">
              <div className="max-w-xl border border-gold-500/20 bg-ink-paper/40 p-8" data-testid="security-panel">
                <div className="flex items-center gap-3 mb-2">
                  <ShieldCheck size={20} className="text-gold-500" />
                  <h3 className="font-serif text-2xl text-white">Change password</h3>
                </div>
                <p className="text-sm text-white/55 mb-6">
                  Keep your admin access secure. Use a strong password (min 8 characters, mix of letters, numbers &amp; symbols recommended).
                </p>
                <form onSubmit={changePassword} className="space-y-4" data-testid="change-password-form">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">Current password</label>
                    <Input
                      required
                      type="password"
                      autoComplete="current-password"
                      value={pwd.current_password}
                      onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                      data-testid="current-password-input"
                      className="bg-ink border-gold-500/25 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">New password</label>
                    <Input
                      required
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={pwd.new_password}
                      onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                      data-testid="new-password-input"
                      className="bg-ink border-gold-500/25 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">Confirm new password</label>
                    <Input
                      required
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={pwd.confirm_password}
                      onChange={(e) => setPwd({ ...pwd, confirm_password: e.target.value })}
                      data-testid="confirm-password-input"
                      className="bg-ink border-gold-500/25 text-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={pwdSaving}
                    data-testid="change-password-submit"
                    className="bg-gold-500 text-ink hover:bg-gold-400 font-semibold w-full"
                  >
                    <KeyRound size={15} className="mr-1.5" />
                    {pwdSaving ? "Updating…" : "Update password"}
                  </Button>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}